import * as React from 'react';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import EnterpriseAdminPermissionsProvider from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { ENTERPRISE_MODAL_NAMES } from 'bundles/unified-common/constants/enterprise';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { useEnterpriseOrgScopedModalManager } from 'bundles/unified-common/providers/org-scoped/modals/EnterpriseOrgScopedModalManagerContext';
import OrgScopedChangePreviewProgramsModal from 'bundles/unified-home-common/components/enterprise/modals/OrgScopedChangePreviewProgramsModal';
import OrgScopedEmailVerifyModal from 'bundles/unified-home-common/components/enterprise/modals/OrgScopedEmailVerifyModal';
import OrgScopedForcedSSORecoveryEmailModal from 'bundles/unified-home-common/components/enterprise/modals/OrgScopedForcedSSORecoveryEmailModal';

const EnterpriseTermsOfUseModal = createLoadableComponent(
  () => import('bundles/unified-common/components/enterprise/EnterpriseTermsOfUseModal')
);

/**
 * Renders org-scoped enterprise modals based on the modal manager context.
 *
 * Currently supports:
 * - EnterpriseTermsOfUseModal
 * - OrgScopedForcedSSORecoveryEmailModal (ADD_EMAIL)
 *
 * For program-scoped modals, see bundles/unified-common/components/EnterpriseModalManager.tsx
 *
 * TODO: The following modals from EnterpriseModalManager are program-scoped
 * and would require program data to be migrated here:
 * - AutoenrolledCoursesModal (requires program.id, program.slug, autoEnrolledCourses)
 * - Login redirect (modifies router query params)
 */

const EnterpriseOrgScopedModalManager = () => {
  const modalManager = useEnterpriseOrgScopedModalManager();
  const thirdPartyOrganization = useThirdPartyOrganization();

  // Don't render anything if org data is not available
  if (!thirdPartyOrganization) {
    return null;
  }

  // Render EnterpriseTermsOfUseModal (highest priority)
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ENTERPRISE_TERMS_OF_USE) {
    return (
      <EnterpriseTermsOfUseModal
        showTermsOfUseModal={modalManager.showEnterpriseTermsOfUseModal}
        setShowTermsOfUseModal={modalManager.setShowEnterpriseTermsOfUseModal}
        thirdPartyOrganization={thirdPartyOrganization}
        isEnterpriseAdmin={modalManager.isEnterpriseAdmin}
        shouldShowCCPAMarketingConsent={modalManager.shouldShowCCPAMarketingConsent}
        shouldShowGDPRMarketingConsent={modalManager.shouldShowGDPRMarketingConsent}
        shouldShowThirdPartyTermsOfConsent={modalManager.shouldShowThirdPartyTermsOfConsent}
      />
    );
  }

  // Render EMAIL_VERIFICATION modal (invited/allowlisted learner with an unverified email)
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.EMAIL_VERIFICATION) {
    return <OrgScopedEmailVerifyModal />;
  }

  // Render CHANGE_PREVIEW_PROGRAMS modal (user-triggered, takes priority over auto-shown modals)
  if (modalManager?.showChangePreviewProgramsModal) {
    return (
      <EnterpriseAdminPermissionsProvider thirdPartySlug={thirdPartyOrganization.slug}>
        <OrgScopedChangePreviewProgramsModal />
      </EnterpriseAdminPermissionsProvider>
    );
  }

  // Render ADD_EMAIL modal (lowest priority)
  if (modalManager?.modalToShow === ENTERPRISE_MODAL_NAMES.ADD_EMAIL) {
    return <OrgScopedForcedSSORecoveryEmailModal />;
  }

  // TODO: Add rendering logic for program-scoped modals when migrating:
  // - LOGIN -> router.push with authMode: 'login'
  // - AUTOENROLLED_COURSES -> AutoenrolledCoursesModal

  return null;
};

const EnterpriseOrgScopedModalManagerWithErrorBoundary = () => {
  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseOrgScopedModalManager">
      <EnterpriseOrgScopedModalManager />
    </ErrorBoundaryWithTagging>
  );
};

export default EnterpriseOrgScopedModalManagerWithErrorBoundary;
